/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { generateUuid } from "../string/generateUuid";
import { objectForEach } from "../object/objectForEach";

export type PubSubDefinition<Data extends object = object> = Record<
  string,
  Data
>;

type CallbackAll<EventKey = string, Data = object> = (
  key: EventKey,
  data: Data,
) => void;

export type PubSubCallback<Data = object> = (data: Data) => void;

/**
 * PubSub is a class that provides a publish/subscribe pattern.
 */
export class PubSub<T extends PubSubDefinition> {
  private keyEvents: Partial<Record<keyof T, PubSubCallback[]>>;
  private allEvents: Partial<Record<string, CallbackAll>>;

  constructor() {
    this.keyEvents = {};
    this.allEvents = {};
  }

  subscribe<EventKey extends keyof T>(
    event: EventKey,
    callback: PubSubCallback<T[EventKey]>,
  ): () => void {
    if (!this.keyEvents[event]) {
      this.keyEvents[event] = [];
    }
    this.keyEvents[event].push(callback as PubSubCallback<object>);

    return () => {
      this.keyEvents[event] =
        this.keyEvents?.[event]?.filter((cb) => cb !== callback) ?? [];
    };
  }

  publish<EventKey extends keyof T>(event: EventKey, data: T[EventKey]): void {
    // console.log("[PubSub] publish", event, data);

    if (this.keyEvents?.[event]) {
      this.keyEvents[event].forEach((callback) => {
        callback(data);
      });
    }

    objectForEach(this.allEvents, ([, callback]) =>
      callback?.(event as string, data),
    );
  }

  subscribeAll<EventKey extends keyof T>(
    callback: CallbackAll<EventKey, T[EventKey]>,
  ): () => void {
    const id = generateUuid();
    this.allEvents[id as string] = callback as CallbackAll<string, any>;

    return () => {
      delete this.allEvents[id];
    };
  }
}
